import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Settings, Headphones, Sparkles } from 'lucide-react'
import { getWindowRegistry, resolveQuickWindows } from '../hooks/useNavigation'
import { useQuickActionsStore } from '../stores/quickActionsStore'
import { useSidebarThemeStore } from '../stores/sidebarThemeStore'
import { SIDEBAR_THEMES, DEFAULT_SIDEBAR_THEME } from '../theme/sidebarThemes'
import { ONDERBALK_HOOGTE } from '../theme/onderbalk'
import SupportPanel from './support/SupportPanel'
import LanguageSwitcher from './LanguageSwitcher'
import SidebarThemeToggle from './SidebarThemeToggle'
import ThemeToggle from './ThemeToggle'

/**
 * OnderBalk — de vaste balk over de VOLLE vensterbreedte, onderaan (Zoho-model). Absorbeert de vroegere
 * floating SnelknoppenBalk (er is dus geen tweede snelknoppen-bron meer) én de kop-controls van de sidebar.
 *
 * RECHTS, in één groep (gap-1): de personaliseerbare snelknoppen · tandwiel-kiezer · vaste tools Support +
 * AI · de app-controls taal/sidebar-skin/dark-light. LINKS blijft leeg (later status/records-info).
 *
 * Hoogte + achtergrond/rand komen uit één bron: ONDERBALK_HOOGTE en de skin-token (sb.balk/divider/kopIcon).
 * Snelknop-routes komen uit de vensterregistratie (SSOT) → nooit een dode knop.
 */
export default function OnderBalk({ onOpenAI }) {
  const { t } = useTranslation()
  const location = useLocation()
  const quickIds = useQuickActionsStore((s) => s.quickIds)
  const toggleQuick = useQuickActionsStore((s) => s.toggleQuick)
  const resetQuick = useQuickActionsStore((s) => s.resetQuick)
  const actiefThema = useSidebarThemeStore((s) => s.actiefThema)
  const sb = SIDEBAR_THEMES[actiefThema] || SIDEBAR_THEMES[DEFAULT_SIDEBAR_THEME]

  const [kiezerOpen, setKiezerOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)

  const vensters = useMemo(() => resolveQuickWindows(quickIds, t), [quickIds, t])
  const groepen = useMemo(() => {
    const map = new Map()
    for (const w of getWindowRegistry(t)) {
      if (!w.quickEligible) continue
      if (!map.has(w.groepId)) map.set(w.groepId, { id: w.groepId, label: w.groepLabel, windows: [] })
      map.get(w.groepId).windows.push(w)
    }
    return [...map.values()]
  }, [t])

  const isActief = (route) =>
    route === '/dashboard' ? location.pathname === '/dashboard' : location.pathname.startsWith(route)

  // Gedeeld icoon-knop-uiterlijk: klikvlak ~32px (p-2), icoon 18px, skin-neutrale hover, erft de kop-kleur.
  const knop = 'p-2 rounded-lg flex items-center justify-center transition-colors hover:bg-blue-500/10'
  const ico = 'w-[18px] h-[18px]'
  const scheiding = <div className="w-px h-5 bg-current opacity-20 mx-1 shrink-0" />

  return (
    <>
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 flex items-center justify-end gap-1 px-3 border-t ${sb.balk} ${sb.divider} ${sb.kopIcon}`}
        style={{ height: ONDERBALK_HOOGTE }}
      >
        {/* LINKS blijft leeg — later status/records-info. */}

        {/* Snelknoppen (uit de voorkeur; onbekende/dubbele/niet-kiesbare id's zijn al geweerd). */}
        {vensters.map((w) => {
          const Icon = w.icon
          const actief = isActief(w.route)
          return (
            <Link
              key={w.id}
              to={w.route}
              title={w.label}
              aria-label={w.label}
              className={`${knop} ${actief ? 'bg-blue-500/15' : ''}`}
            >
              {Icon ? <Icon className={ico} /> : null}
            </Link>
          )
        })}

        {/* Tandwiel-kiezer (popover opent OMHOOG boven de balk). */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setKiezerOpen((v) => !v)}
            title={t('snelknoppen.kiezen', { defaultValue: 'Snelknoppen kiezen' })}
            aria-label={t('snelknoppen.kiezen', { defaultValue: 'Snelknoppen kiezen' })}
            className={`${knop} ${kiezerOpen ? 'bg-blue-500/15' : ''}`}
          >
            <Settings className={ico} />
          </button>
          {kiezerOpen && (
            <>
              <button
                type="button"
                aria-hidden
                tabIndex={-1}
                onClick={() => setKiezerOpen(false)}
                className="fixed inset-0 z-40 cursor-default"
              />
              <div className="absolute bottom-full right-0 mb-2 w-72 max-h-[70vh] overflow-y-auto z-50 bg-surface dark:bg-gray-800 rounded-card border border-line dark:border-gray-700 shadow-card p-3 text-ink dark:text-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-muted uppercase tracking-wider">
                    {t('snelknoppen.kiezerTitel', { defaultValue: 'Snelknoppen kiezen' })}
                  </span>
                  <button type="button" onClick={resetQuick} className="text-xs font-semibold text-accent hover:underline">
                    {t('snelknoppen.standaard', { defaultValue: 'Standaard' })}
                  </button>
                </div>
                {groepen.map((g) => (
                  <div key={g.id} className="mb-2">
                    <div className="text-[0.65rem] font-semibold text-muted uppercase tracking-wider px-1 mb-1">
                      {g.label}
                    </div>
                    {g.windows.map((w) => {
                      const Icon = w.icon
                      const aan = quickIds.includes(w.id)
                      return (
                        <label
                          key={w.id}
                          className="flex items-center gap-2 px-1 py-1 rounded-control hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={aan}
                            onChange={() => toggleQuick(w.id)}
                            className="w-4 h-4 accent-blue-600"
                          />
                          {Icon ? <Icon className="w-4 h-4 text-muted shrink-0" /> : null}
                          <span className="text-sm truncate">{w.label}</span>
                        </label>
                      )
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {scheiding}

        {/* Vaste tools: Support + AI. */}
        <button
          type="button"
          onClick={() => setSupportOpen(true)}
          title={t('support.openen', { defaultValue: 'Support — melding of vraag' })}
          aria-label={t('support.openen', { defaultValue: 'Support openen' })}
          className={knop}
        >
          <Headphones className={ico} />
        </button>
        {onOpenAI && (
          <button
            type="button"
            onClick={() => onOpenAI()}
            title={t('layout.ai.openen', { defaultValue: 'Orion AI-assistent' })}
            aria-label={t('layout.ai.openen', { defaultValue: 'Orion AI-assistent' })}
            className={knop}
          >
            <Sparkles className={ico} />
          </button>
        )}

        {scheiding}

        {/* App-controls: taal · sidebar-skin · dark/light (erven de kop-kleur via text-current). */}
        <LanguageSwitcher />
        <SidebarThemeToggle />
        <ThemeToggle variant="simple" />
      </div>

      {/* Support-drawer (geabsorbeerd uit de oude SupportWidget/SnelknoppenBalk). */}
      <SupportPanel isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  )
}
